'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Heart, MapPin, Eye, X } from 'lucide-react';
import { jsnService } from '@/lib/supabase/jsnService';
import type { PreviewProfile } from '@/lib/supabase/types';

const statusConfig: Record<string, { label: string; className: string }> = {
  online: { label: 'Online', className: 'status-online' },
  new: { label: 'New', className: 'status-new' },
  verified: { label: 'Verified', className: 'status-verified' },
  premium: { label: 'Premium', className: 'status-premium' },
  offline: { label: '', className: '' },
};

export default function ProfilePreviews() {
  const [likedIds, setLikedIds] = useState<Set<string>>(new Set());
  const [profiles, setProfiles] = useState<PreviewProfile[]>([]);

  useEffect(() => {
    let cancelled = false;
    jsnService
      .getVisiblePreviewProfiles()
      .then(async (data) => {
        const slice = data.slice(0, 6);
        const withPhotos = await jsnService.enrichVisiblePreviewPhotos(slice);
        if (!cancelled) setProfiles(withPhotos);
      })
      .catch(() => {
        // silently fail — section simply won't render profiles
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleLike = (id: string) => {
    setLikedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  if (profiles.length === 0) return null;

  return (
    <section id="profiles" className="py-24 bg-background">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 2xl:px-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="space-y-3">
            <div className="text-xs font-semibold tracking-widest uppercase text-primary">
              Member Profiles
            </div>
            <h2 className="section-header">Meet Kenyan Singles Near You</h2>
            <p className="text-muted-foreground text-lg max-w-lg">
              Connect with Kenyan diaspora members seeking meaningful relationships across the USA.
            </p>
          </div>
          <Link
            href="/discover-page"
            className="btn-primary px-6 py-3 rounded-xl text-sm font-semibold whitespace-nowrap flex-shrink-0"
          >
            Browse All Profiles
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-5">
          {profiles.map((profile) => {
            const isLiked = likedIds.has(profile.id);

            return (
              <div
                key={profile.id}
                className="bg-card border border-border rounded-2xl overflow-hidden card-hover group"
              >
                {/* Avatar / Photo header */}
                <div
                  className="h-40 flex items-center justify-center relative overflow-hidden"
                  style={{ background: `linear-gradient(135deg, ${profile.avatarColor}22, ${profile.avatarColor}44)` }}
                >
                  {profile.photoUrl ? (
                    <img
                      src={profile.photoUrl}
                      alt={`Profile photo of ${profile.displayName}`}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    <div
                      className="w-20 h-20 rounded-2xl flex items-center justify-center text-white font-bold text-2xl shadow-lg"
                      style={{ background: `linear-gradient(135deg, ${profile.avatarColor}, ${profile.avatarColor}bb)` }}
                    >
                      {profile.avatarInitials}
                    </div>
                  )}
                </div>

                {/* Card body */}
                <div className="p-5 space-y-3">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-foreground text-base">
                        {profile.displayName}, {profile.age}
                      </h3>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin size={11} />
                      {profile.usState} · {profile.kenyanCounty}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    <span className="filter-chip">{profile.intention}</span>
                    <span className="filter-chip">{profile.faith.split(' — ')[0]}</span>
                  </div>

                  <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                    {profile.bio}
                  </p>

                  {/* Interests */}
                  <div className="flex flex-wrap gap-1">
                    {profile.interests.slice(0, 2).map((interest) => (
                      <span key={`${profile.id}-int-${interest}`} className="text-xs text-muted-foreground bg-muted/40 px-2 py-0.5 rounded-md">
                        {interest}
                      </span>
                    ))}
                    {profile.interests.length > 2 && (
                      <span className="text-xs text-muted-foreground bg-muted/40 px-2 py-0.5 rounded-md">
                        +{profile.interests.length - 2} more
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={() => toggleLike(profile.id)}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isLiked
                          ? '' :'bg-muted/30 border border-border text-muted-foreground hover:border-primary hover:text-primary'
                      }`}
                      style={isLiked ? { backgroundColor: 'rgba(0,128,0,0.2)', border: '1px solid #008000', color: '#008000' } : {}}
                      aria-label={isLiked ? 'Unlike profile' : 'Like profile'}
                    >
                      <Heart size={14} style={isLiked ? { fill: '#008000', color: '#008000' } : {}} />
                      {isLiked ? 'Liked' : 'Like'}
                    </button>
                    <button
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium bg-muted/30 border border-border text-muted-foreground hover:border-border hover:text-foreground transition-all"
                      aria-label="Pass on profile"
                    >
                      <X size={14} />
                      Pass
                    </button>
                    <Link
                      href="/discover-page"
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium text-white transition-all duration-200"
                      style={{ backgroundColor: '#750000' }}
                      aria-label="View full profile"
                    >
                      <Eye size={14} />
                      View
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <Link
            href="/sign-up-login-screen"
            className="btn-primary px-10 py-4 rounded-xl text-base font-semibold inline-flex items-center gap-2"
          >
            Join JSN to Browse All Profiles
          </Link>
          <p className="text-muted-foreground text-sm mt-3">
            Free to join · Browse member profiles · Upgrade to message
          </p>
        </div>
      </div>
    </section>
  );
}